import { LitElement, html, css, nothing } from 'lit';
import { StoreMixin } from '../store.js';
import { api } from '../api.js';
import { navigate } from '../router.js';
import { money, count, mediumDate } from '../lib/format.js';

/**
 * The Estimates screen (Estimate Tool 5; Phase E6): every estimate, newest first.
 *
 * Three decisions the design makes and this screen keeps:
 *
 * **The new-estimate link sits below the table, not above it.** The table is the screen's
 * subject; when there are forty estimates the reader scrolls, and the link is where the list
 * ends.
 *
 * **The empty state names the usable pool.** A first-time reader should learn how much history
 * is behind the number they are about to see *before* they price anything — the tool is only as
 * good as the analogs behind it, and saying so up front is more honest than a blank table.
 *
 * **Nothing deletes.** The `…` menu offers Edit, Duplicate and Archive; archiving is reversible
 * and the archived rows are one filter away.
 */
class EstimateList extends StoreMixin(LitElement) {
  static properties = {
    _data: { state: true },
    _error: { state: true },
    _busy: { state: true },
    _menu: { state: true },
    _showArchived: { state: true },
  };

  constructor() {
    super();
    this._data = null;
    this._error = null;
    this._busy = new Set();
    this._menu = null;
    this._showArchived = false;
  }

  static styles = css`
    :host {
      display: block;
    }
    .card {
      background: var(--surface);
      border: 1px solid var(--border-soft);
      border-radius: var(--radius-card);
      box-shadow: var(--shadow-card);
      padding: 20px 24px 18px;
    }
    .eyebrow {
      font-family: var(--font-eyebrow);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: var(--ink-muted);
    }
    h2 {
      margin: 6px 0 4px;
      font-size: 20px;
      color: var(--ink-strong);
    }
    .lede {
      margin: 0 0 16px;
      color: var(--ink-muted);
      font-size: 13.5px;
      max-width: 68ch;
    }
    .controls {
      display: flex;
      align-items: center;
      gap: 8px 16px;
      flex-wrap: wrap;
      margin-bottom: 12px;
    }
    .toggle {
      font: inherit;
      font-size: 12.5px;
      font-weight: 600;
      padding: 4px 12px;
      border: 1px solid var(--control-border);
      border-radius: var(--radius-pill);
      background: var(--control-bg);
      color: var(--ink-strong);
      cursor: pointer;
    }
    .shown {
      font-size: 12.5px;
      color: var(--ink-muted);
    }
    .scroll {
      overflow-x: auto;
    }
    table {
      border-collapse: collapse;
      width: 100%;
      font-size: 13px;
    }
    th,
    td {
      text-align: left;
      padding: 8px 10px;
      border-bottom: 1px solid var(--border-soft);
      white-space: nowrap;
    }
    th {
      font-size: 11.5px;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: var(--ink-muted);
      font-weight: 700;
    }
    td.num,
    th.num {
      text-align: right;
      font-variant-numeric: tabular-nums;
    }
    tbody tr:hover {
      background: var(--surface-2);
    }
    tr.archived td {
      opacity: 0.6;
    }
    a.row {
      color: var(--ink-strong);
      font-weight: 600;
      text-decoration: none;
    }
    a.row:hover {
      text-decoration: underline;
    }
    .client {
      color: var(--ink);
    }
    /* "Other": an estimate for somebody who is not a Workamajig client yet. */
    .other {
      display: inline-block;
      margin-left: 6px;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--ink-muted);
      border: 1px solid var(--border);
      border-radius: var(--radius-pill);
      padding: 0 6px;
    }
    .status {
      font-size: 11.5px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: var(--ink-muted);
    }
    .status.final {
      color: var(--status-healthy);
    }
    .menu-cell {
      position: relative;
      width: 1%;
    }
    .dots {
      font: inherit;
      font-size: 16px;
      line-height: 1;
      padding: 2px 8px;
      border: 1px solid transparent;
      border-radius: 6px;
      background: none;
      color: var(--ink-muted);
      cursor: pointer;
    }
    .dots:hover {
      border-color: var(--control-border);
      color: var(--ink-strong);
    }
    .menu {
      position: absolute;
      right: 8px;
      top: 34px;
      min-width: 150px;
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: 8px;
      box-shadow: var(--shadow-menu);
      overflow: hidden;
      z-index: 20;
    }
    .menu button {
      display: block;
      width: 100%;
      text-align: left;
      font: inherit;
      font-size: 13px;
      padding: 8px 14px;
      border: 0;
      background: none;
      color: var(--ink);
      cursor: pointer;
    }
    .menu button:hover {
      background: var(--surface-2);
    }
    .menu button[disabled] {
      opacity: 0.5;
      cursor: not-allowed;
    }
    /* Below the table, because the table is the subject (Estimate Tool 5). */
    .new {
      margin-top: 18px;
      padding-top: 14px;
      border-top: 1px solid var(--border-soft);
    }
    .new a {
      display: inline-block;
      font-size: 14px;
      font-weight: 700;
      color: var(--surface);
      background: var(--ink-strong);
      border-radius: 6px;
      padding: 9px 18px;
      text-decoration: none;
    }
    .empty {
      padding: 22px 0 6px;
      color: var(--ink-muted);
      max-width: 62ch;
      line-height: 1.55;
    }
    .empty strong {
      color: var(--ink-strong);
    }
    .state,
    .error {
      padding: 16px 0;
      color: var(--ink-muted);
    }
    .error {
      color: var(--status-overloaded);
      font-weight: 600;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this._load();
    this._onOutside = (e) => {
      if (this._menu && !e.composedPath().includes(this)) this._menu = null;
    };
    document.addEventListener('pointerdown', this._onOutside);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('pointerdown', this._onOutside);
  }

  get _canWrite() {
    return ['planner', 'admin'].includes(this.appState.user?.effective_role);
  }

  async _load() {
    try {
      this._data = await api.get(
        '/estimate/estimates',
        this._showArchived ? { archived: true } : {},
      );
      this._error = null;
    } catch (err) {
      this._error = `Could not load estimates: ${err.message}`;
    }
  }

  async _act(row, what) {
    this._menu = null;
    this._busy = new Set([...this._busy, row.id]);
    try {
      if (what === 'duplicate') {
        const res = await api.post(`/estimate/estimates/${row.id}/duplicate`, {});
        await this._load();
        navigate(`/estimate/${res.estimate.id}`);
        return;
      }
      await api.put(`/estimate/estimates/${row.id}/archive`, {
        archived: row.status !== 'archived',
      });
      await this._load();
      this._error = null;
    } catch (err) {
      this._error = `Could not ${what} ${row.project_name}: ${err.message}`;
    } finally {
      const next = new Set(this._busy);
      next.delete(row.id);
      this._busy = next;
    }
  }

  _go(event, path) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    navigate(path);
  }

  async _toggleArchived() {
    this._showArchived = !this._showArchived;
    await this._load();
  }

  _row(e) {
    const busy = this._busy.has(e.id);
    const archived = e.status === 'archived';
    return html`
      <tr class=${archived ? 'archived' : ''}>
        <td class="client">
          ${e.company_name}${e.is_new_company ? html`<span class="other">Other</span>` : nothing}
        </td>
        <td>
          <a
            class="row"
            href="/estimate/${e.id}"
            @click=${(ev) => this._go(ev, `/estimate/${e.id}`)}
            >${e.project_name}</a
          >
        </td>
        <td class="num">${e.org_complexity} / ${e.human_factor}</td>
        <td class="num">${e.result ? money(e.result.total_amount) : '—'}</td>
        <td><span class="status ${e.status}">${e.status}</span></td>
        <td>
          ${e.updated_at ? mediumDate(e.updated_at.slice(0, 10)) : '—'}
          ${e.updated_by ? html`· ${e.updated_by}` : nothing}
        </td>
        <td class="menu-cell">
          ${
            this._canWrite
              ? html`
                  <button
                    class="dots"
                    aria-haspopup="menu"
                    aria-expanded=${this._menu === e.id ? 'true' : 'false'}
                    aria-label="Actions for ${e.project_name}"
                    ?disabled=${busy}
                    @click=${() => (this._menu = this._menu === e.id ? null : e.id)}
                  >
                    …
                  </button>
                  ${
                    this._menu === e.id
                      ? html`<div class="menu" role="menu">
                          <button
                            role="menuitem"
                            ?disabled=${archived}
                            @click=${(ev) => this._go(ev, `/estimate/${e.id}`)}
                          >
                            Edit
                          </button>
                          <button role="menuitem" @click=${() => this._act(e, 'duplicate')}>
                            Duplicate
                          </button>
                          <button role="menuitem" @click=${() => this._act(e, 'archive')}>
                            ${archived ? 'Restore' : 'Archive'}
                          </button>
                        </div>`
                      : nothing
                  }
                `
              : nothing
          }
        </td>
      </tr>
    `;
  }

  _newLink() {
    return html`
      <div class="new">
        <a href="/estimate/new" @click=${(e) => this._go(e, '/estimate/new')}>New estimate</a>
      </div>
    `;
  }

  render() {
    if (this._error && !this._data)
      return html`<div class="card"><p class="error">${this._error}</p></div>`;
    if (!this._data) return html`<div class="card"><p class="state">Loading…</p></div>`;

    const rows = this._data.estimates;

    return html`
      <div class="card">
        <div class="eyebrow">Estimates</div>
        <h2>What we have priced</h2>
        ${this._error ? html`<p class="error">${this._error}</p>` : nothing}
        ${
          rows.length === 0 && !this._showArchived
            ? html`
                <p class="empty">
                  No estimate yet. When you price one, it will be matched against
                  <strong
                    >${count(this._data.pool_size)} past website
                    ${this._data.pool_size === 1 ? 'project' : 'projects'}</strong
                  >
                  — the ones with a rated client, a completion date and a billed total.
                  ${
                    this._data.pool_size === 0
                      ? html`There are none yet, so nothing can be priced until Data Hygiene has
                        some clients rated.`
                      : html`Data Hygiene is where that number gets bigger.`
                  }
                </p>
                ${this._newLink()}
              `
            : html`
                <div class="controls">
                  <button class="toggle" @click=${() => this._toggleArchived()}>
                    ${this._showArchived ? 'Hide archived' : 'Show archived'}
                  </button>
                  <span class="shown">
                    ${count(rows.length)} ${rows.length === 1 ? 'estimate' : 'estimates'} ·
                    ${count(this._data.pool_size)} comparable projects behind each price
                  </span>
                </div>
                <div class="scroll">
                  <table>
                    <thead>
                      <tr>
                        <th scope="col">Company</th>
                        <th scope="col">Project</th>
                        <th scope="col" class="num">Complexity / Human</th>
                        <th scope="col" class="num">Total</th>
                        <th scope="col">Status</th>
                        <th scope="col">Updated</th>
                        <th scope="col"><span class="sr-only">Actions</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      ${rows.map((e) => this._row(e))}
                    </tbody>
                  </table>
                </div>
                ${this._newLink()}
              `
        }
      </div>
    `;
  }
}

customElements.define('estimate-list', EstimateList);
